import { type JSX,SyntheticEvent, useEffect, useState } from 'react';
import { Outlet, useLocation,useParams } from 'react-router-dom';

import { CircularProgress, Tabs,Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { NotFound } from 'common/components/NotFound';
import { Page } from 'common/components/Page';
import { selectPageInfo, setPageInfo } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ResourceDetailHeader } from 'common/components/ResourceDetailHeader';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { StyledTab } from 'common/components/StyledTab';
import { Routes, RoutesNameMap } from 'common/constants/routes';
import { useAuthContext } from 'common/contexts/AuthContext';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchGlobalRoles } from 'common/services/api/GlobalRoles/GlobalRoles.thunks';
import { fetchTenantDetails } from 'common/services/api/Tenants/Tenants.thunks';
import { FetchTenantDetailsResponse } from 'common/services/api/Tenants/TenantThunkTypeDefs';
import { isDisabledRoute } from 'common/utils/DisabledRouteEvaluator';
import { replaceLocation } from 'common/utils/locationHelpers';

export function TenantDetails(): JSX.Element {
    const [currentTab, setCurrentTab] = useState<Routes>(Routes.Users);
    const [processing, setProcessing] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const { currentUserRoles } = useAuthContext();

    const location = useLocation();
    const params = useParams();

    useEffect(() => {
        const replacedLastSegment = replaceLocation(
            lastPathSegment => lastPathSegment === params?.tenantId,
            `${location.pathname}/${Routes.Users}`);
        const tabLink: Routes =  [Routes.Acls, Routes.AclAccesses, Routes.AclResources].includes(replacedLastSegment as Routes)
            ? Routes.Acls
            : replacedLastSegment as Routes;
        setCurrentTab(tabLink);
    }, [location]);

    const dispatch = useAppDispatch();
    useEffect(() => {
        const fetchParams = {
            tenantId: params?.tenantId ?? '',
            applicationSuiteId: params?.suiteId ?? ''
        };
        dispatch(fetchTenantDetails(fetchParams))
            .unwrap()
            .then((result) => {
                dispatch(setPageInfo(result));
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, []);

    useEffect(() => {
        dispatch(fetchGlobalRoles()).catch(console.error);
    }, []);

    const getTabLink = (key: string) => (
        `/${Routes.ApplicationSuites}/${params.suiteId}/${Routes.Tenants}/${params.tenantId}/${key}`
    );

    const handleChangeTab = (_event: SyntheticEvent, newValue: Routes) => {
        setCurrentTab(newValue);
    };

    const tenantDetails = useAppSelector(selectPageInfo) as FetchTenantDetailsResponse;
    const errorCode = extractStatusCode(error);

    if (errorCode === 404) {
        return <NotFound />;
    }

    if (errorCode && errorCode !== 200) {
        return <p>Something went wrong</p>;
    }

    if(processing) {
        return <CircularProgress color='primary'/>;
    }

    return (
        <Page
            title={`${tenantDetails?.name} - ${RoutesNameMap.get(Routes.Tenant)}`}
            header={(
                <Typography variant="h5" gutterBottom component="div">
                    {tenantDetails?.id &&
                        <StyledBreadcrumbs
                            guidNameMappings={[{
                                resourceId: params.suiteId ?? '',
                                resourceName: tenantDetails?.applicationSuiteName ?? ''
                            },  {
                                resourceId: params.tenantId ?? '',
                                resourceName: tenantDetails?.name ?? ''
                            }]}
                        /> }
                    {tenantDetails?.id &&
                        <ResourceDetailHeader
                            resourceType='Tenant'
                            resourceName={tenantDetails?.name || ''}
                            resourceId={params.tenantId ?? ''}
                        />}
                </Typography>
            )}
        >
            <Typography
                variant="subtitle1" gutterBottom
                component="div"
                textAlign='left'
                color="info.light">
                License Required: {tenantDetails?.tenantRequiresLicense? 'Yes':'No'}
            </Typography>
            <Tabs
                sx={{ mb: '-60px', zIndex:1200, width: '45rem' }}
                onChange={handleChangeTab}
                value={currentTab}
            >
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Users)}
                    key={Routes.Users}
                    link={getTabLink(Routes.Users)}
                    value={Routes.Users}
                />
                {tenantDetails?.tenantRequiresLicense && <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Licenses)}
                    key={Routes.Licenses}
                    link={getTabLink(Routes.Licenses)}
                    value={Routes.Licenses}
                />}
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.UserGroups)}
                    key={Routes.UserGroups}
                    link={getTabLink(Routes.UserGroups)}
                    value={Routes.UserGroups}
                />
                <StyledTab
                    disabled={isDisabledRoute(Routes.Roles, currentUserRoles)}
                    label={RoutesNameMap.get(Routes.Roles)}
                    key={Routes.Roles}
                    link={getTabLink(Routes.Roles)}
                    value={Routes.Roles}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Claims)}
                    key={Routes.Claims}
                    link={getTabLink(Routes.Claims)}
                    value={Routes.Claims}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Acls)}
                    key={Routes.Acls}
                    link={getTabLink(`${Routes.Acls}/${Routes.AclResources}`)}
                    value={Routes.Acls}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.AuthorizedIssuers)}
                    key={Routes.AuthorizedIssuers}
                    link={getTabLink(Routes.AuthorizedIssuers)}
                    value={Routes.AuthorizedIssuers}
                />
            </Tabs>
            <Outlet />
        </Page>
    );
}
